import {DEMO_REQUEST_PATH} from '@/components/www/demo-request/demoPage'

export const LISTING_REJECTIONS_PATH = '/listing-rejections'

/**
 * Opening the audit form is a route query, not just local state, so the landing
 * header's single CTA (a link, see LandingHeader.vue) can trigger it and so the
 * form survives a shared/pasted URL.
 */
export const AUDIT_QUERY_KEY = 'audit'

/** MarketingContact#submission_form for every submission from this page. */
export const AUDIT_CONTACT_SUBMISSION_FORM = 'www_listing_rejections'

/**
 * Same booking destination as /sell-on-nordstrom (the demo-request page, or the
 * Calendly popup once NORDSTROM_CALENDLY_URL is set) with this page's own
 * `source` so the two landing pages stay separable in analytics.
 */
export function listingRejectionsDemoPath(cta: string): string {
    return `${DEMO_REQUEST_PATH}?source=listing-rejections&cta=${encodeURIComponent(cta)}`
}

/**
 * MarketingContact requires `name` (HasHumanName → validates_name), but this form
 * deliberately asks for four things and a contact name isn't one of them. Derive a
 * brand identifier from the store URL instead so Slack/Mailtrap still have
 * something readable. `validates_name` rejects anything containing "://", so the
 * scheme has to come off.
 */
export function brandNameFromStoreUrl(storeUrl: string): string {
    const bare = storeUrl.trim().replace(/^https?:\/\//i, '').replace(/^www\./i, '')
    return bare.split(/[/?#]/)[0] || 'Unknown brand'
}

/** A point with a bold lead-in sentence, then supporting copy. */
export interface LeadInPoint {
    id: string
    lead: string
    body: string
}

export interface FaqItem {
    id: string
    question: string
    answer: string
}

/**
 * Marketplace required-field counts quoted in the "three reasons" section.
 *
 * Client-supplied figures, NOT read from our taxonomy API — unlike the counts on
 * /sell-on-nordstrom, which bind to `useTaxonomyQuery`. The two are not the same
 * measurement: `Taxonomy#fields_count` is *total* published fields (Nordstrom
 * reports 241), while these are *required* fields. Verify against a marketplace
 * spec before changing either number, and don't "reconcile" them by pointing
 * this at fields_count — that would silently relabel a total as a requirement.
 */
export const REQUIRED_FIELD_COUNTS = {
    nordstrom: '250',
    macys: '1,077',
} as const

/**
 * Video testimonial shown as the page's trust block.
 *
 * Same Vimeo ID, poster and loop clip as the Universal Standard case study, so
 * there's one source of truth for the recording — see
 * `case-studies/universal-standard/Page.vue`. Stephanie's line is about leaving
 * an old way of working behind, which is exactly this page's audience.
 */
export const TESTIMONIAL = {
    vimeoId: '1206223811',
    title: 'Three minutes on what it was like before.',
    eyebrow: 'Osello Client Success Story',
    quote: "I can't imagine convincing myself, or anyone on the team, to go back to how we did it before.",
    author: 'Stephanie Nelson',
    role: 'Senior Director of Operations, Universal Standard',
    /** Sits below the role, separated, as the relationship label. */
    badge: 'Osello Client',
    caseStudyLabel: 'See full case study',
    caseStudyHref: '/case-studies/universal-standard',
} as const

export const LISTING_REJECTIONS_LP = {
    meta: {
        title: 'Fix your marketplace listing rejections',
        description:
            "Already selling on Nordstrom, Macy's or Target Plus and hitting rejections? Send us one live style and we'll show you which required fields your current setup is missing.",
    },
    hero: {
        title: 'Your listings keep getting rejected. Nobody will tell you why.',
        body: [
            "You're already on Nordstrom. The hard part was supposed to be over.",
            "Send us one live style. We'll map it against every required field and send back what your setup isn't sending. No call required.",
        ],
        cta: 'Get my listing audit',
        /** Sits under the CTA as reassurance, not as a second action. */
        ctaNote: 'Free. One business day. No call required.',
    },
    /**
     * Figures shown under the hero.
     *
     * Sourced from the two published case studies, and attributed the same way as
     * the /sell-on-nordstrom stat strip so the sibling pages can't contradict each
     * other. NOTE: `case-studies/shared/caseStudies.ts` files the 5,000+ SKU and
     * 3-week figures under Furniq UK, while this attribution (and the client copy
     * both pages were written from) credits the 5,000+ to Universal Standard. One
     * of the two is wrong; confirm with the case-study owner before reusing these
     * anywhere new.
     */
    statsBar: {
        label: 'Measured on real catalogs',
        items: [
            {id: 'approval', value: '93%', label: 'Universal Standard · listings approved first pass'},
            {id: 'skus', value: '5,000+', label: 'Universal Standard · SKUs syncing daily'},
            {id: 'furniq-time', value: '3 weeks', label: 'Furniq UK · live on Nordstrom'},
        ],
    },
    /**
     * Retailer logo row. Deliberately says where our clients are live rather than
     * claiming a partner or certification status — Osello holds no published
     * marketplace partner badge, and inventing one is a claim we can't stand
     * behind. Swap in real badge artwork here if that changes.
     */
    trustRow: {
        label: 'Brands running on Osello are live on',
    },
    /** Repeated CTA band between sections. Names the exchange rather than listing
     *  its terms — two verbless fragments beside a button didn't say what you send
     *  or what comes back. */
    ctaBand: {
        note: "Send one live style. We'll show you the fields it's missing.",
    },
    reasons: {
        title: 'Three reasons a listing fails, and none of them are your product.',
        points: [
            {
                id: 'field',
                lead: "A field your feed doesn't send.",
                body: `Nordstrom publishes ${REQUIRED_FIELD_COUNTS.nordstrom} required fields. Macy's publishes ${REQUIRED_FIELD_COUNTS.macys}. Generic product feeds were built for products with one SKU and one spec sheet.`,
            },
            {
                id: 'image',
                lead: "An image that's wrong there.",
                body: "Ghost mannequin on one marketplace, on-model on the next, pure white background on both. Your photography isn't wrong. It's wrong for that destination.",
            },
            {
                id: 'variant',
                lead: "A variant that didn't map.",
                body: "Eight sizes, six colors, forty-eight records. If one size chart ID doesn't resolve, the whole style stalls.",
            },
        ] satisfies LeadInPoint[],
    },
    continuity: {
        title: 'Nothing goes dark while you move.',
        intro: 'The question everyone asks first, so here it is before you have to.',
        points: [
            {
                id: 'live',
                lead: 'Your live listings stay live.',
                body: 'We build alongside your current setup and cut over once the mapping is verified, not before.',
            },
            {
                id: 'account',
                lead: 'Your Nordstrom account is yours.',
                body: 'You are not reapplying and your standing does not reset. This is a connection change, not a relationship change.',
            },
            {
                id: 'orders',
                lead: 'In-flight orders keep flowing.',
                body: 'Orders land in Shopify the way they already do, throughout.',
            },
        ] satisfies LeadInPoint[],
    },
    /**
     * The index eyebrows run 01-03 across pricing, comparison and specialty only.
     * The two photo sections above them carry no eyebrow, so numbering that
     * started at 03 would read as a sequence with its first two entries missing.
     */
    pricing: {
        eyebrow: '01 · What it costs',
        title: 'Software, not a percentage of your sales.',
        body: [
            'Agencies charge an onboarding fee, then a commission on every order, forever.',
            'Osello is a flat monthly fee. You keep 100% of what you sell.',
            'Others will manage your marketplace listings. We do it without taking a cut of the orders you already win.',
        ],
        /** Side-by-side contrast card labels; the copy above is the argument. */
        compare: [
            {id: 'agency', label: 'Typical agency', headline: 'Onboarding fee, then a cut of every order', note: 'Forever, as you grow'},
            {id: 'osello', label: 'Osello', headline: 'Flat monthly fee', note: 'No commission, no revenue share'},
        ],
    },
    /**
     * Side-by-side comparison against the two alternatives this page's audience is
     * actually on — a rule-and-feed integration app, or a managed agency.
     *
     * Deliberately NOT named companies. Two reasons: (1) no page on the public site
     * names a competitor, so naming two here would be a new posture, not a copy
     * tweak; (2) every cell is a claim about someone else's product, and the only
     * competitor list we hold internally (BRAND_GUIDE.md) is five unranked names
     * with no verified feature or pricing detail behind them. Category columns are
     * substantiated by our own published positioning — the row content tracks
     * `db/content/help_articles/getting-started/how-osello-compares-to-feed-apps.md`
     * and the agency terms already described in the pricing section above.
     *
     * If we do decide to name providers, swap `columns[].name`/`kicker` and re-verify
     * every cell against that provider's current public docs first.
     */
    comparison: {
        eyebrow: '02 · Side by side',
        title: "How this compares to what you're on now.",
        intro: 'Most brands reach us from one of two places. Here is where the difference actually shows up.',
        columns: [
            {id: 'feed', name: 'Feed Management Apps', kicker: 'Legacy Tools'},
            {id: 'agency', name: 'Manual', kicker: 'Agency, Offshore'},
            /** `wordmark` swaps the column name for the Osello mark — the name is still the accessible label. */
            {id: 'osello', name: 'Osello', kicker: 'Agentic Listing Agents', isUs: true, wordmark: true},
        ],
        rows: [
            {
                id: 'rejection',
                label: 'When one listing gets rejected',
                values: {
                    feed: "Change a global rule, and hope it doesn't break another style.",
                    agency: 'Email the request, wait for someone to key it in.',
                    osello: 'Open that listing. Fix that field. Nothing else moves.',
                },
            },
            {
                id: 'records',
                label: 'Where your listings live',
                values: {
                    feed: 'Nowhere you can open. A listing is whatever the rule outputs.',
                    agency: 'In their account, in their spreadsheet.',
                    osello: 'Every listing is a stored record you can open and edit.',
                },
            },
            {
                id: 'fields',
                label: 'Marketplace-required fields',
                values: {
                    feed: 'You map them, or add marketplace columns to your source catalog.',
                    agency: 'Staff fill in spreadsheet templates on your behalf.',
                    osello: 'AI writes them per listing and asks you when it isn\'t sure.',
                },
            },
            {
                id: 'variants',
                label: 'Size runs and colorways',
                values: {
                    feed: 'Built for one SKU and one spec sheet. Variants are rows to map.',
                    agency: 'Handled by hand, every drop.',
                    osello: '5,000+ SKUs syncing daily, without a spreadsheet.',
                },
            },
            {
                id: 'price',
                label: 'What you pay',
                values: {
                    feed: 'Flat monthly fee, tiered by volume.',
                    agency: 'Onboarding fee, then a commission on every order.',
                    osello: 'Flat monthly fee. You keep 100% of what you sell.',
                },
            },
            {
                id: 'setup',
                label: 'Who does the work',
                values: {
                    feed: 'You build the rules, then you maintain them.',
                    agency: 'They do, on their timeline.',
                    osello: 'We do, with granular control the moment you want it.',
                },
            },
        ],
        footnote:
            'Describes the two models brands most often move to Osello from, not any single provider. Commercial terms vary; the agency row describes the onboarding-fee-plus-commission arrangement we see most often in fashion.',
    },
    specialty: {
        eyebrow: '03 · Why fashion',
        title: 'Fashion is the specialty, not a vertical we support.',
        body: 'Size runs, colorways, seasonal drops, imagery standards. Universal Standard runs 5,000+ SKUs through Osello daily without a spreadsheet.',
        quote: "It's the first marketplace tool that actually understands our catalog.",
        quoteAuthor: 'Arin Cubuk',
        quoteRole: 'Founder, Furniq UK',
        quoteBadge: 'Osello Client',
        quoteCaseStudyLabel: 'See full case study',
        quoteCaseStudyHref: '/case-studies/furniq-uk',
    },
    faq: {
        title: 'Questions, answered.',
        items: [
            {
                id: 'what-you-get',
                question: 'What does the audit actually give me?',
                answer: "A field-by-field list of what your current feed sends and what the marketplace requires, for one style you choose. It's yours whether you hire us or not.",
            },
            {
                id: 'how-long',
                question: 'How long does a migration take?',
                answer: "We won't put a date on your catalog before we've seen it. The audit tells us both, and it's free.",
            },
            {
                id: 'leave-provider',
                question: 'Do we have to leave our current provider first?',
                answer: "No. Nothing gets canceled until the new mapping is verified and you've seen it working.",
            },
            {
                id: 'not-just-nordstrom',
                question: "We're not just on Nordstrom.",
                answer: "Neither is the problem. The same mapping covers Macy's, Bloomingdale's and Target Plus.",
            },
            {
                id: 'setup-is-fine',
                question: 'What if the audit says our current setup is fine?',
                answer: "Then we'll tell you that. It happens, and you keep the audit.",
            },
        ] satisfies FaqItem[],
    },
    closing: {
        title: "Send us one style. We'll tell you what's missing.",
        body: "One style, one afternoon, no call required. If you'd rather talk it through first,",
        /** Sentence continues from `body` into this link, so it carries no leading capital. */
        bodyLink: 'book fifteen minutes instead',
        cta: 'Get my listing audit',
    },
    audit: {
        title: 'Get my listing audit',
        body: "One live style, mapped against every required field. We'll email you the list of what your current setup isn't sending.",
        submit: 'Send me my audit',
        successTitle: "Got it. We're on it.",
        successBody:
            "We'll map your style against every required field and email you the list, usually within one business day. No call required.",
    },
} as const
